import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  HiUsers,
  HiUser,
  HiTruck,
  HiStar,
  HiPlus,
  HiBell,
  HiLogout,
  HiEye,
  HiTrendingUp,
  HiClock,
  HiLocationMarker,
  HiPhone,
  HiSparkles,
  HiExclamation,
  HiSearch,
  HiPencil,
  HiDocument,
  HiCheckCircle,
  HiXCircle,
  HiRefresh,
  HiShieldCheck
} from 'react-icons/hi';
import { FaWallet, FaMoneyBillWave } from 'react-icons/fa';
import { API_URL } from '../../config/apiConfig';
import VendorWalletCard from '../../components/Vendor/VendorWalletCard';

import VendorDriverWalletTable from '../../components/Vendor/VendorDriverWalletTable';
import VendorCommissionPanel from '../../components/Vendor/VendorCommissionPanel';
import VendorCommissionCard from '../../components/Vendor/VendorCommissionCard';
import VendorWalletDisplay from '../../components/Vendor/VendorWalletDisplay';
import { formatDateDDMMYYYY } from '../../utils/dateUtils';

const VendorDashboard = () => {
  const dispatch = useDispatch();
  const [vendorData, setVendorData] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ fullName: '', businessName: '', phone: '', state: '', city: '', street: '', landmark: '', pinCode: '' });
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);

  // Booking state
  const [bookings, setBookings] = useState([]);
  const [bookingStats, setBookingStats] = useState({});
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningDriver, setAssigningDriver] = useState(false);
  const [bookingFilter, setBookingFilter] = useState('all');
  const [bookingPage, setBookingPage] = useState(1);
  const bookingPageSize = 10;
  const [bookingPagination, setBookingPagination] = useState({ currentPage: 1, totalPages: 1, totalBookings: 0 });

  const itemsPerPage = 10;
  const navigate = useNavigate();

  // Handle wallet navigation -> dedicated pages
  const handleWalletNavigation = (walletPage) => {
    if (walletPage === 'wallet') {
      navigate('/vendor/wallet/history');
    } else if (walletPage === 'commission') {
      navigate('/vendor/wallet/withdraw');
    }
  };

  useEffect(() => {
    checkAuth();
    fetchDashboardData();

    // Auto-refresh every 30 seconds to show real-time driver status updates
    const refreshInterval = setInterval(() => {
      fetchDashboardData();
    }, 30000); // 30 seconds

    // Cleanup interval on component unmount
    return () => {
      clearInterval(refreshInterval);
    };
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('vendorToken');
    const vendor = localStorage.getItem('vendorData');
    
    if (!token || !vendor) {
      navigate('/vendor/login');
      return;
    }

    try {
      setVendorData(JSON.parse(vendor));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } catch (error) {
      navigate('/vendor/login');
    }
  };

  const fetchDashboardData = async () => {
    try {
      const vendor = JSON.parse(localStorage.getItem('vendorData'));
      if (!vendor) return;

      // Fetch drivers, stats, and bookings
      const [driversRes, statsRes, bookingsRes] = await Promise.all([
        axios.get(`${API_URL}/vendor/${vendor._id}/drivers?limit=5`),
        axios.get(`${API_URL}/vendor/${vendor._id}/drivers/stats`),
        axios.get(`${API_URL}/vendor/${vendor._id}/bookings?page=${1}&limit=${bookingPageSize}&status=${bookingFilter}`)
      ]);

      if (driversRes.data.status) {
        setDrivers(driversRes.data.data.drivers || []);
      }

      if (statsRes.data.status) {
        setStats(statsRes.data.data || {});
      }

      if (bookingsRes.data.success) {
        setBookings(bookingsRes.data.data.bookings || []);
        setBookingStats(bookingsRes.data.data.summary || {});
        const pg = bookingsRes.data.data.pagination || {};
        setBookingPagination({
          currentPage: pg.currentPage || 1,
          totalPages: pg.totalPages || 1,
          totalBookings: pg.totalBookings || 0
        });
        setBookingPage(pg.currentPage || 1);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorBookings = async (page = bookingPage, status = bookingFilter) => {
    try {
      const vendor = JSON.parse(localStorage.getItem('vendorData'));
      if (!vendor) return;
      const res = await axios.get(`${API_URL}/vendor/${vendor._id}/bookings?page=${page}&limit=${bookingPageSize}&status=${status}`);
      if (res.data.success) {
        setBookings(res.data.data.bookings || []);
        setBookingStats(res.data.data.summary || {});
        const pg = res.data.data.pagination || {};
        setBookingPagination({
          currentPage: pg.currentPage || page,
          totalPages: pg.totalPages || 1,
          totalBookings: pg.totalBookings || 0
        });
        setBookingPage(pg.currentPage || page);
      }
    } catch (e) {
      console.error('Error fetching vendor bookings:', e);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('vendorToken');
    localStorage.removeItem('vendorData');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/vendor/login');
  };

  // Submit updated profile to backend
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('vendorToken');
      const payload = {
        fullName: profileForm.fullName,
        businessName: profileForm.businessName,
        phone: profileForm.phone,
        state: profileForm.state,
        city: profileForm.city,
        completeAddress: {
          street: profileForm.street,
          landmark: profileForm.landmark,
          pinCode: profileForm.pinCode
        }
      };

      const res = await axios.put(`${API_URL}/vendor/profile`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data && res.data.status) {
        const updated = res.data.data;
        setVendorData(updated);
        localStorage.setItem('vendorData', JSON.stringify(updated));
        toast.success(res.data.message || 'Profile updated successfully');
        setShowEditProfile(false);
      } else {
        toast.error(res.data?.message || 'Failed to update profile');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to update profile';
      toast.error(msg);
    }
  };

  // Filter drivers when search term or filters change
  useEffect(() => {
    filterDrivers();
  }, [drivers, searchTerm, statusFilter, currentPage]);

  // Reset page when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  // Fetch bookings when tab/filter/page changes
  useEffect(() => {
    if (activeTab === 'bookings') {
      fetchVendorBookings(bookingPage, bookingFilter);
    }
  }, [activeTab, bookingPage, bookingFilter]);

  const filterDrivers = () => {
    let filtered = drivers;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter((driver) => {
        const nameMatch = driver.name?.toLowerCase().includes(searchTerm.toLowerCase());
        const phoneMatch = driver.phone?.includes(searchTerm);
        const vehicleMatch = driver.vehicleNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            driver.vehicleModel?.toLowerCase().includes(searchTerm.toLowerCase());
        return nameMatch || phoneMatch || vehicleMatch;
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((driver) => {
        switch (statusFilter) {
          case 'available':
            return driver.isAvailable;
          case 'unavailable':
            return !driver.isAvailable;
          case 'verified':
            return driver.documentsVerified;
          case 'unverified':
            return !driver.documentsVerified;
          default:
            return true;
        }
      });
    }

    // Apply pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedDrivers = filtered.slice(startIndex, endIndex);

    setFilteredDrivers(paginatedDrivers);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const toggleDriverStatus = async (driverId, currentStatus) => {
    try {
      const token = localStorage.getItem('vendorToken');
      await axios.put(
        `${API_URL}/vendor/drivers/${driverId}/status`,
        { isAvailable: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Driver status updated successfully');
      fetchDashboardData(); // Refresh the list
    } catch (error) {
      toast.error('Failed to update driver status');
    }
  };

  const editDriver = (driver) => {
    setSelectedDriver(driver);
    setShowEditModal(true);
  };

  const viewDriverDocuments = (driver) => {
    setSelectedDriver(driver);
    setShowDocumentModal(true);
  };

  // Booking assignment functions
  const openAssignModal = (booking) => {
    setSelectedBooking(booking);
    setShowAssignModal(true);
  };

  const assignDriverToBooking = async (driverId) => {
    if (!selectedBooking || !vendorData) return;

    setAssigningDriver(true);
    try {
      const token = localStorage.getItem('vendorToken');
      const response = await axios.post(
        `${API_URL}/vendor/${vendorData._id}/bookings/${selectedBooking._id}/assign-driver`,
        { driverId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Driver assigned to booking successfully!`);
        setShowAssignModal(false);
        setSelectedBooking(null);
        fetchDashboardData(); // Refresh data
      } else {
        toast.error('Failed to assign driver to booking');
      }
    } catch (error) {
      console.error('Error assigning driver:', error);
      toast.error(error.response?.data?.message || 'Failed to assign driver');
    } finally {
      setAssigningDriver(false);
    }
  };

  const getTotalPages = () => {
    const totalFiltered = drivers.filter((driver) => {
      let matches = true;
      if (searchTerm) {
        matches = driver.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 driver.phone?.includes(searchTerm) ||
                 driver.vehicleNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 driver.vehicleModel?.toLowerCase().includes(searchTerm.toLowerCase());
      }
      if (statusFilter !== 'all' && matches) {
        switch (statusFilter) {
          case 'available':
            matches = driver.isAvailable;
            break;
          case 'unavailable':
            matches = !driver.isAvailable;
            break;
          case 'verified':
            matches = driver.documentsVerified;
            break;
          case 'unverified':
            matches = !driver.documentsVerified;
            break;
        }
      }
      return matches;
    }).length;
    return Math.ceil(totalFiltered / itemsPerPage);
  };

  const getAvailabilityColor = (isAvailable) => {
    return isAvailable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  const getAvailabilityText = (isAvailable) => {
    return isAvailable ? 'Available' : 'Offline';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
          <p className="text-indigo-600 font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Enhanced Custom Header */}
      <header className="bg-white/80 backdrop-blur-lg shadow-xl border-b border-white/20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-20 justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 flex items-center justify-center shadow-lg">
                <HiSparkles className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Urban Ride
                </h1>
                <p className="text-sm text-gray-600 font-medium">Vendor Portal</p>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <div className="hidden sm:block text-right">
                <p className="text-sm text-gray-600">Welcome back,</p>
                <p className="font-semibold text-gray-900">{vendorData?.fullName || vendorData?.email}</p>
                {vendorData?.email && <p className="text-sm text-gray-500">{vendorData.email}</p>}
              </div>
              <button className="relative p-2 text-gray-600 hover:text-indigo-600 transition-colors duration-200">
                <HiBell className="h-6 w-6" />
                <span className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full"></span>
              </button>

              <button
                onClick={() => {
                  setProfileForm({
                    fullName: vendorData?.fullName || '',
                    businessName: vendorData?.businessName || '',
                    phone: vendorData?.phone || '',
                    state: vendorData?.state || '',
                    city: vendorData?.city || '',
                    street: vendorData?.completeAddress?.street || '',
                    landmark: vendorData?.completeAddress?.landmark || '',
                    pinCode: vendorData?.completeAddress?.pinCode || ''
                  });
                  setShowEditProfile(true);
                }}
                className="inline-flex items-center px-3 py-2 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-colors duration-200 font-medium mr-2"
              >
                <HiPencil className="h-4 w-4 mr-2" />
                Edit Profile
              </button>

              <button onClick={() => {
                  // If vendor is logged in (vendorData present), navigate to change-password, otherwise to forgot-password/send-otp
                  if (vendorData) navigate('/vendor/change-password');
                  else navigate('/vendor/forgot-password/send-otp');
                }} className="inline-flex items-center px-3 py-2 bg-amber-50 text-amber-600 rounded-xl hover:bg-amber-100 transition-colors duration-200 font-medium mr-2">
                <HiShieldCheck className="h-4 w-4 mr-2" />
                Change Password
              </button>

              <button
                onClick={handleLogout}
                className="inline-flex items-center px-4 py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors duration-200 font-medium"
              >
                <HiLogout className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">

        {/* Edit Profile Modal */}
        {showEditProfile && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Edit Profile</h3>
                <button onClick={() => setShowEditProfile(false)} className="text-gray-500">Close</button>
              </div>

              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    value={vendorData?.email || ''}
                    readOnly
                    className="mt-1 block w-full rounded-md border-gray-200 bg-gray-50 text-gray-700 shadow-sm p-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Full name</label>
                  <input
                    type="text"
                    value={profileForm.fullName}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, fullName: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Business name</label>
                  <input
                    type="text"
                    value={profileForm.businessName}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, businessName: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <input
                      type="tel"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">State</label>
                    <input
                      type="text"
                      value={profileForm.state}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, state: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">City</label>
                    <input
                      type="text"
                      value={profileForm.city}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, city: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">PIN Code</label>
                    <input
                      type="text"
                      value={profileForm.pinCode}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, pinCode: e.target.value }))}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Street / Address</label>
                  <input
                    type="text"
                    value={profileForm.street}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, street: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Landmark</label>
                  <input
                    type="text"
                    value={profileForm.landmark}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, landmark: e.target.value }))}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2"
                  />
                </div>

                <div className="flex justify-end space-x-2">
                  <button type="button" onClick={() => setShowEditProfile(false)} className="px-4 py-2 rounded bg-gray-100">Cancel</button>
                  <button type="submit" className="px-4 py-2 rounded bg-indigo-600 text-white">Save changes</button>
                </div>
              </form>

            </div>
          </div>
        )}

        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden cursor-pointer" onClick={() => { setActiveTab('drivers'); setStatusFilter('all'); }}>
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <HiUsers className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <dt className="text-sm font-medium text-gray-600 truncate">
                    Total Drivers
                  </dt>
                  <dd className="text-3xl font-bold text-gray-900">
                    {drivers.length}
                  </dd>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden cursor-pointer" onClick={() => { setActiveTab('drivers'); setStatusFilter('available'); }}>
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                    <HiUser className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <dt className="text-sm font-medium text-gray-600 truncate">
                    Available Drivers
                  </dt>
                  <dd className="text-3xl font-bold text-gray-900">
                    {drivers.filter(driver => driver.isAvailable).length}
                  </dd>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden cursor-pointer" onClick={() => { setActiveTab('drivers'); setStatusFilter('verified'); }}>
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-cyan-500 to-cyan-600 flex items-center justify-center">
                    <HiCheckCircle className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <dt className="text-sm font-medium text-gray-600 truncate">
                    Verified Drivers
                  </dt>
                  <dd className="text-3xl font-bold text-gray-900">
                    {drivers.filter(driver => driver.documentsVerified).length}
                  </dd>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden cursor-pointer" onClick={() => { setActiveTab('bookings'); setBookingFilter('all'); }}>
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                    <HiTruck className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <dt className="text-sm font-medium text-gray-600 truncate">
                    Total Rides
                  </dt>
                  <dd className="text-3xl font-bold text-gray-900">
                    {stats.totalRides || 0}
                  </dd>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Booking Status Summary */
        }
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 border border-blue-200 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1 cursor-pointer" onClick={() => { setActiveTab('bookings'); setBookingFilter('pending'); }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600 uppercase tracking-wide">Pending Assignment</p>
                <p className="text-3xl font-bold text-blue-900 mt-2">{bookingStats.pending || 0}</p>
                <p className="text-xs text-blue-500 mt-1">Requires your action</p>
              </div>
              <div className="p-3 bg-blue-500 rounded-xl shadow-lg">
                <HiClock className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl p-6 border border-yellow-200 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1 cursor-pointer" onClick={() => { setActiveTab('bookings'); setBookingFilter('assigned'); }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600 uppercase tracking-wide">Driver Assigned</p>
                <p className="text-3xl font-bold text-yellow-900 mt-2">{bookingStats.assigned || 0}</p>
                <p className="text-xs text-yellow-500 mt-1">Ready to start</p>
              </div>
              <div className="p-3 bg-yellow-500 rounded-xl shadow-lg">
                <HiUsers className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-6 border border-orange-200 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1 cursor-pointer" onClick={() => { setActiveTab('bookings'); setBookingFilter('inProgress'); }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-orange-600 uppercase tracking-wide">Trip Active</p>
                <p className="text-3xl font-bold text-orange-900 mt-2">{bookingStats.inProgress || 0}</p>
                <p className="text-xs text-orange-500 mt-1">Currently in progress</p>
              </div>
              <div className="p-3 bg-orange-500 rounded-xl shadow-lg">
                <HiLocationMarker className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6 border border-green-200 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-1 cursor-pointer" onClick={() => { setActiveTab('bookings'); setBookingFilter('completed'); }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600 uppercase tracking-wide">Completed</p>
                <p className="text-3xl font-bold text-green-900 mt-2">{bookingStats.completed || 0}</p>
                <p className="text-xs text-green-500 mt-1">Successfully finished</p>
              </div>
              <div className="p-3 bg-green-500 rounded-xl shadow-lg">
                <HiCheckCircle className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Tabs */}
        <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6 overflow-x-auto">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-4 px-1 border-b-2 font-semibold text-sm transition-colors duration-200 whitespace-nowrap ${
                  activeTab === 'overview'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('drivers')}
                className={`py-4 px-1 border-b-2 font-semibold text-sm transition-colors duration-200 whitespace-nowrap ${
                  activeTab === 'drivers'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                All Drivers
              </button>
              <button
                onClick={() => setActiveTab('wallet')}
                className={`py-4 px-1 border-b-2 font-semibold text-sm transition-colors duration-200 whitespace-nowrap flex items-center ${
                  activeTab === 'wallet'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FaWallet className="mr-1" />
                Wallet System
              </button>
              <button
                onClick={() => setActiveTab('commission')}
                className={`py-4 px-1 border-b-2 font-semibold text-sm transition-colors duration-200 whitespace-nowrap flex items-center ${
                  activeTab === 'commission'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FaMoneyBillWave className="mr-1" />
                Commission
              </button>
              <button
                onClick={() => setActiveTab('bookings')}
                className={`py-4 px-1 border-b-2 font-semibold text-sm transition-colors duration-200 whitespace-nowrap ${
                  activeTab === 'bookings'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Bookings
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Vendor Wallet Display with real data */}
                <div>
                  <VendorWalletDisplay
                    vendorId={vendorData?._id}
                    onNavigateToWallet={handleWalletNavigation}
                  />
                </div>

                {/* Recent Drivers */}
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">
                        Recent Drivers
                      </h3>
                      <p className="text-sm text-gray-600">
                        Latest driver registrations in your fleet
                      </p>
                    </div>
                    {vendorData?.verificationStatus === 'approved' && (
                      <button
                        onClick={() => navigate('/vendor/drivers/add')}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 font-semibold shadow-lg transform hover:scale-105"
                      >
                        <HiPlus className="h-5 w-5 mr-2" />
                        Add Driver
                      </button>
                    )}
                  </div>
                  
                  {drivers.length > 0 ? (
                    <div className="space-y-4">
                      {drivers.slice(0, 5).map((driver) => (
                        <div key={driver._id} className="bg-gray-50 rounded-2xl p-4 hover:bg-gray-100 transition-colors duration-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center text-white font-bold text-lg">
                                {driver.name.charAt(0)}
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900">
                                  {driver.name}
                                </div>
                                <div className="text-sm text-gray-600 flex items-center space-x-4">
                                  <span className="flex items-center">
                                    <HiPhone className="h-4 w-4 mr-1" />
                                    {driver.phone}
                                  </span>
                                  <span className="flex items-center">
                                    <HiTruck className="h-4 w-4 mr-1" />
                                    {driver.vehicleNumber}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3">
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getAvailabilityColor(driver.isAvailable)}`}>
                                {getAvailabilityText(driver.isAvailable)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="mx-auto h-24 w-24 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center mb-4">
                        <HiTruck className="h-12 w-12 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        No drivers yet
                      </h3>
                      <p className="text-gray-600 mb-6">
                        {vendorData?.verificationStatus === 'approved' 
                          ? 'Start building your fleet by adding your first driver'
                          : 'Add drivers once your account is approved'
                        }
                      </p>
                      {vendorData?.verificationStatus === 'approved' && (
                        <button
                          onClick={() => navigate('/vendor/drivers/add')}
                          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 font-semibold"
                        >
                          <HiPlus className="h-5 w-5 mr-2" />
                          Add Your First Driver
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'drivers' && (
              <div>
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      All Drivers
                    </h3>
                    <p className="text-sm text-gray-600">
                      Manage your complete driver fleet
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                    {/* Search Input */}
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search drivers..."
                        value={searchTerm}
                        onChange={handleSearch}
                        className="w-full sm:w-64 pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                      <HiSearch className="absolute left-3 top-3 text-gray-400" />
                    </div>

                    {/* Status Filter */}
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full sm:w-48 px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                      <option value="all">All Drivers</option>
                      <option value="available">Available</option>
                      <option value="unavailable">Unavailable</option>
                      <option value="verified">Documents Verified</option>
                      <option value="unverified">Documents Unverified</option>
                    </select>

                    <button
                      onClick={() => navigate('/vendor/drivers/add')}
                      className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 font-semibold shadow-lg transform hover:scale-105 whitespace-nowrap"
                    >
                      <HiPlus className="h-5 w-5 mr-2" />
                      Add Driver
                    </button>
                  </div>
                </div>

                {filteredDrivers.length > 0 ? (
                  <div>
                    <div className="overflow-hidden rounded-2xl border border-gray-200 mb-6">
                      <table className="min-w-full divide-y divide-gray-200 bg-white">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Driver
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Vehicle
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Documents
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Rides
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Rating
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredDrivers.map((driver) => (
                            <tr key={driver._id} className="hover:bg-gray-50 transition-colors duration-200">
                              <td className="px-3 py-2 whitespace-nowrap align-middle">
                                <div className="flex items-center">
                                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center text-white font-bold">
                                    {driver.name?.charAt(0) || 'D'}
                                  </div>
                                  <div className="ml-4">
                                    <div className="font-semibold text-gray-900">
                                      {driver.name || 'Unknown'}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                      {driver.email || 'No email'}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                      {driver.phone || 'No phone'}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap align-middle">
                                <div className="font-medium text-gray-900">
                                  {driver.vehicleNumber || 'N/A'}
                                </div>
                                <div className="text-sm text-gray-600">
                                  {driver.vehicleModel || 'N/A'}
                                </div>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap align-middle">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${getAvailabilityColor(driver.isAvailable)}`}>
                                  {getAvailabilityText(driver.isAvailable)}
                                </span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap align-middle">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                  driver.documentsVerified
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {driver.documentsVerified ? 'Verified' : 'Pending'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                                {driver.totalRides || 0}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap align-middle">
                                <div className="flex items-center">
                                  <HiStar className="h-4 w-4 text-yellow-400 mr-1" />
                                  <span className="font-medium text-gray-900">
                                    {driver.ratings ? driver.ratings.toFixed(1) : '0.0'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap align-middle">
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => toggleDriverStatus(driver._id, driver.isAvailable)}
                                    className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                                      driver.isAvailable
                                        ? 'bg-red-100 text-red-800 hover:bg-red-200'
                                        : 'bg-green-100 text-green-800 hover:bg-green-200'
                                    }`}
                                    title={driver.isAvailable ? 'Mark Unavailable' : 'Mark Available'}
                                  >
                                    {driver.isAvailable ? 'Disable' : 'Enable'}
                                  </button>

                                  <button
                                    onClick={() => editDriver(driver)}
                                    className="px-3 py-1 rounded-lg text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                                    title="View Driver Details"
                                  >
                                    <HiEye className="h-3 w-3" />
                                  </button>

                                  <button
                                    onClick={() => viewDriverDocuments(driver)}
                                    className="px-3 py-1 rounded-lg text-xs font-medium bg-indigo-100 text-indigo-800 hover:bg-indigo-200 transition-colors"
                                    title="View Documents"
                                  >
                                    <HiDocument className="h-3 w-3" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {getTotalPages() > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-lg">
                        <div className="flex items-center text-sm text-gray-500">
                          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, drivers.length)} of {drivers.length} drivers
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Previous
                          </button>

                          {Array.from({ length: getTotalPages() }, (_, i) => i + 1).map((page) => (
                            <button
                              key={page}
                              onClick={() => setCurrentPage(page)}
                              className={`px-3 py-1 text-sm border rounded-md ${
                                currentPage === page
                                  ? 'bg-indigo-600 text-white border-indigo-600'
                                  : 'border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          ))}

                          <button
                            onClick={() => setCurrentPage(Math.min(getTotalPages(), currentPage + 1))}
                            disabled={currentPage === getTotalPages()}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-200">
                    <div className="bg-gradient-to-br from-indigo-100 to-purple-100 p-8 rounded-full mb-6">
                      <HiUser className="h-16 w-16 text-indigo-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {searchTerm || statusFilter !== 'all'
                        ? 'No Drivers Match Your Search'
                        : 'No Drivers Yet'
                      }
                    </h3>
                    <p className="text-gray-600 mb-6 text-center max-w-md">
                      {searchTerm || statusFilter !== 'all'
                        ? 'Try adjusting your search terms or filters'
                        : 'Start building your fleet by adding your first driver'
                      }
                    </p>
                    {(!searchTerm && statusFilter === 'all') && (
                      <button
                        onClick={() => navigate('/vendor/drivers/add')}
                        className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 font-semibold"
                      >
                        <HiPlus className="h-5 w-5 mr-2" />
                        Add Your First Driver
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'wallet' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                  <div className="lg:col-span-1">
                    <VendorWalletCard
                      vendorId={vendorData?._id}
                      onNavigateToWallet={handleWalletNavigation}
                      commissionOnly={true}
                    />
                  </div>
                  <div className="lg:col-span-2">
                    <div className="bg-white rounded-xl shadow-lg p-2">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 px-4 pt-4">
                        Dynamic Commission Management
                      </h3>
                      <VendorCommissionPanel
                        vendorId={vendorData?._id}
                        vendorData={vendorData}
                      />
                      {/* Debug vendor data */}
                      {process.env.NODE_ENV === 'development' && (
                        <div className="mt-4 p-2 bg-gray-100 rounded text-xs">
                          Debug: vendorId = {vendorData?._id || 'undefined'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <VendorDriverWalletTable vendorId={vendorData?._id} />
              </div>
            )}

            {activeTab === 'commission' && (
              <div className="space-y-6">
                <VendorCommissionPanel
                  vendorId={vendorData?._id}
                  vendorData={vendorData}
                />
                {/* Debug vendor data */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="p-2 bg-gray-100 rounded text-xs">
                    Debug: vendorId = {vendorData?._id || 'undefined'}, token = {localStorage.getItem('vendorToken') ? 'exists' : 'missing'}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'bookings' && (
              <div className="space-y-6">
                {/* Bookings List */}
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">
                        Assigned Bookings
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Manage and assign drivers to your bookings
                      </p>
                    </div>
                    <button
                      onClick={() => fetchVendorBookings(bookingPage, bookingFilter)}
                      className="inline-flex items-center px-4 py-2 bg-indigo-100 text-indigo-700 rounded-xl hover:bg-indigo-200 transition-colors font-medium text-sm"
                    >
                      <HiRefresh className="h-4 w-4 mr-2" />
                      Refresh
                    </button>
                  </div>

                  {bookingFilter !== 'all' && (
                    <div className="mb-4 flex items-center">
                      <span className="px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs mr-2">
                        Filter: {bookingFilter === 'pending' ? 'Pending Assignment' : bookingFilter === 'assigned' ? 'Driver Assigned' : bookingFilter === 'inProgress' ? 'Trip Active' : bookingFilter === 'completed' ? 'Completed' : 'All'}
                      </span>
                      <button onClick={() => setBookingFilter('all')} className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200">Clear</button>
                    </div>
                  )}
                  {bookings.filter(b => (
                    bookingFilter === 'all' ? true :
                    bookingFilter === 'pending' ? b.status === 'assigned_to_vendor' :
                    bookingFilter === 'assigned' ? b.status === 'assigned' :
                    bookingFilter === 'inProgress' ? b.status === 'inProgress' :
                    bookingFilter === 'completed' ? b.status === 'completed' : true
                  )).length > 0 ? (
                    <div className="overflow-x-auto rounded-2xl border border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200 bg-white">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Booking
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Passenger
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Route
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Car Type
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Ride Type
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Date & Time
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Assigned Driver
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {bookings
                            .filter(b => (
                              bookingFilter === 'all' ? true :
                              bookingFilter === 'pending' ? b.status === 'assigned_to_vendor' :
                              bookingFilter === 'assigned' ? b.status === 'assigned' :
                              bookingFilter === 'inProgress' ? b.status === 'inProgress' :
                              bookingFilter === 'completed' ? b.status === 'completed' : true
                            ))
                            .map((booking) => (
                            <tr key={booking._id} className="hover:bg-gray-50 transition-colors duration-200">
                              <td className="px-3 py-2 whitespace-nowrap align-middle">
                                <div>
                                  <div className="font-medium text-gray-900">
                                    #{booking.bookingId}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    ₹{booking.totalAmount}
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap align-middle">
                                <div>
                                  <div className="font-medium text-gray-900">
                                    {booking.user?.name || 'N/A'}
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    {booking.user?.phone || 'N/A'}
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-2">
                                <div className="text-sm">
                                  <div className="font-medium text-gray-900">
                                    📍 {(booking.pickupLocation?.name || booking.from)}{booking.pickupLocation?.state ? `, ${booking.pickupLocation.state}` : ''}
                                  </div>
                                  {(() => {
                                    const pickupAddr = booking.pickupAddress;
                                    const pickupLandmark = booking.pickupAddressDetails?.landmark;
                                    const pickupPincode = booking.pickupAddressDetails?.pincode;
                                    if (!pickupAddr && !pickupLandmark && !pickupPincode) return null;
                                    return (
                                      <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
                                        {pickupAddr && (
                                          <span className="px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700">Address: {pickupAddr}</span>
                                        )}
                                        {pickupLandmark && (
                                          <span className="px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700">Landmark: {pickupLandmark}</span>
                                        )}
                                        {pickupPincode && (
                                          <span className="px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700">PIN: {pickupPincode}</span>
                                        )}
                                      </div>
                                    );
                                  })()}
                                  <div className="text-gray-600 mt-1">
                                    🏁 {(booking.dropLocation?.name || booking.to)}{booking.dropLocation?.state ? `, ${booking.dropLocation.state}` : ''}
                                  </div>
                                  {(() => {
                                    const dropAddr = booking.dropAddress;
                                    const dropLandmark = booking.dropAddressDetails?.landmark;
                                    const dropPincode = booking.dropAddressDetails?.pincode;
                                    const additionalInfo = booking.dropAddressDetails?.additionalInfo;
                                    if (!dropAddr && !dropLandmark && !dropPincode && !additionalInfo) return null;
                                    return (
                                      <div className="mt-1 flex flex-wrap gap-1 text-[11px]">
                                        {dropAddr && (
                                          <span className="px-1.5 py-0.5 rounded bg-green-50 border border-green-200 text-green-700">Address: {dropAddr}</span>
                                        )}
                                        {dropLandmark && (
                                          <span className="px-1.5 py-0.5 rounded bg-green-50 border border-green-200 text-green-700">Landmark: {dropLandmark}</span>
                                        )}
                                        {dropPincode && (
                                          <span className="px-1.5 py-0.5 rounded bg-green-50 border border-green-200 text-green-700">PIN: {dropPincode}</span>
                                        )}
                                        {additionalInfo && (
                                          <span className="px-1.5 py-0.5 rounded bg-green-50 border border-green-200 text-green-700">Info: {additionalInfo}</span>
                                        )}
                                      </div>
                                    );
                                  })()}


                                </div>
                              </td>

                              {/* Car Type */}
                              <td className="px-3 py-2 whitespace-nowrap align-middle">
                                <div className="text-xs font-semibold text-purple-700 bg-purple-50 inline-block px-2 py-1 rounded">
                                  🚗 {booking.cabType?.name || booking.cabType || booking.cabName || 'Standard Cab'}
                                </div>
                              </td>

                              {/* Ride Type */}
                              <td className="px-3 py-2 whitespace-nowrap align-middle">
                                <div className={`text-xs inline-block px-2 py-1 rounded font-medium ${
                                  booking.serviceType === 'airport'
                                    ? 'bg-orange-100 text-orange-800'
                                    : booking.tripType === 'roundTrip'
                                    ? 'bg-purple-100 text-purple-800'
                                    : (booking.tripType === 'multiCity' || (booking.viaCities && booking.viaCities.length > 0))
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {(() => {
                                    if (booking.serviceType === 'airport') {
                                      if (booking.transferDirection === 'to' || booking.airportDirection === 'to') {
                                        return '✈️ To Airport';
                                      } else if (booking.transferDirection === 'from' || booking.airportDirection === 'from') {
                                        return '🛬 From Airport';
                                      } else {
                                        return '✈️ Airport Transfer';
                                      }
                                    } else if (booking.serviceType === 'rental') {
                                      return '🏙️ Local Rental';
                                    } else if (booking.tripType === 'roundTrip') {
                                      return '🔄 Round Trip';
                                    } else if (booking.tripType === 'multiCity' || (booking.viaCities && booking.viaCities.length > 0)) {
                                      return '�� Multi-City';
                                    } else {
                                      return '➡️ One Way';
                                    }
                                  })()}
                                </div>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap align-middle">
                                <div className="text-sm">
                                  <div className="font-medium text-gray-900">
                                    {formatDateDDMMYYYY(booking.pickupDate || booking.travelDate || booking.date)}
                                  </div>
                                  <div className="text-gray-600">
                                    {booking.pickupTime}
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap align-middle">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                  booking.status === 'assigned_to_vendor'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : booking.status === 'assigned'
                                    ? 'bg-blue-100 text-blue-800'
                                    : booking.status === 'inProgress'
                                    ? 'bg-orange-100 text-orange-800'
                                    : booking.status === 'completed'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {booking.status === 'assigned_to_vendor' ? 'Pending Assignment' :
                                   booking.status === 'assigned' ? 'Driver Assigned' :
                                   booking.status === 'inProgress' ? 'In Progress' :
                                   booking.status === 'completed' ? 'Completed' : booking.status}
                                </span>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap align-middle">
                                <div className="flex items-center space-x-3 overflow-x-auto scroll-smooth max-w-[240px] pr-2">
                                  {(() => {
                                    const assigned = [];
                                    if (booking.vendorDriver) assigned.push({ type: 'Vendor', ...booking.vendorDriver });
                                    if (assigned.length === 0) {
                                      return <span className="text-gray-400 italic">Not assigned</span>;
                                    }
                                    return assigned.map((d, idx) => (
                                      <div key={idx} className="flex-shrink-0 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 hover:bg-indigo-100 transition">
                                        <div className="flex items-center space-x-2">
                                          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-400 to-purple-400 flex items-center justify-center text-white text-sm font-bold">
                                            {(d.name || 'D').charAt(0)}
                                          </div>
                                          <div>
                                            <div className="text-sm font-medium text-gray-900">{d.name || 'Driver'}</div>
                                            <div className="text-xs text-gray-600">{d.vehicleNumber || d.phone || ''}</div>
                                          </div>
                                        </div>
                                      </div>
                                    ));
                                  })()}
                                </div>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap align-middle">
                                {booking.status === 'assigned_to_vendor' && (
                                  <button
                                    onClick={() => openAssignModal(booking)}
                                    className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-lg text-sm font-semibold hover:from-green-600 hover:to-teal-600 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
                                  >
                                    <HiUsers className="h-4 w-4 mr-2" />
                                    Assign Driver
                                  </button>
                                )}
                                {booking.status !== 'assigned_to_vendor' && (
                                  <div className="flex items-center">
                                    {booking.status === 'assigned' && <HiCheckCircle className="h-4 w-4 text-green-500 mr-2" />}
                                    {booking.status === 'inProgress' && <HiClock className="h-4 w-4 text-orange-500 mr-2" />}
                                    {booking.status === 'completed' && <HiCheckCircle className="h-4 w-4 text-blue-500 mr-2" />}
                                    <span className={`text-xs font-medium ${
                                      booking.status === 'assigned' ? 'text-green-600' :
                                      booking.status === 'inProgress' ? 'text-orange-600' :
                                      booking.status === 'completed' ? 'text-blue-600' : 'text-gray-400'
                                    }`}>
                                      {booking.status === 'assigned' ? 'Driver Assigned' :
                                       booking.status === 'inProgress' ? 'Trip Active' :
                                       booking.status === 'completed' ? 'Trip Completed' : 'N/A'}
                                    </span>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="px-4 py-3 bg-white">
                        {bookingPagination.totalPages > 1 && (
                          <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                            <div className="text-sm text-gray-600">
                              Showing {((bookingPagination.currentPage - 1) * bookingPageSize) + 1} to {Math.min(bookingPagination.currentPage * bookingPageSize, bookingPagination.totalBookings)} of {bookingPagination.totalBookings} bookings
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => setBookingPage(Math.max(1, bookingPagination.currentPage - 1))}
                                disabled={bookingPagination.currentPage <= 1}
                                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Previous
                              </button>
                              {Array.from({ length: bookingPagination.totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                  key={page}
                                  onClick={() => setBookingPage(page)}
                                  className={`px-3 py-1 text-sm border rounded-md ${
                                    bookingPagination.currentPage === page
                                      ? 'bg-indigo-600 text-white border-indigo-600'
                                      : 'border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  {page}
                                </button>
                              ))}
                              <button
                                onClick={() => setBookingPage(Math.min(bookingPagination.totalPages, bookingPagination.currentPage + 1))}
                                disabled={bookingPagination.currentPage >= bookingPagination.totalPages}
                                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-gray-200">
                      <div className="bg-gradient-to-br from-blue-100 to-purple-100 p-8 rounded-full mb-6">
                        <HiDocument className="h-16 w-16 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        No Bookings Assigned
                      </h3>
                      <p className="text-gray-600 text-center max-w-md">
                        When admin assigns bookings to your vendor, they will appear here for you to assign to your drivers.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Driver Modal */}
      {showEditModal && selectedDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4 overflow-y-auto max-h-[90vh]">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">
                Driver Details: {selectedDriver.name || 'Unknown'}
              </h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <HiXCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-600 mb-4">
                View driver information. Contact support for driver modifications.
              </p>

              {/* Availability Status */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Current Status</h3>
                <div className="flex items-center space-x-4">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Availability: </span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      selectedDriver.isAvailable
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedDriver.isAvailable ? 'Available' : 'Unavailable'}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700">Active Status: </span>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      selectedDriver.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedDriver.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Driver Name
                  </label>
                  <input
                    type="text"
                    value={selectedDriver.name || ''}
                    readOnly
                    className="w-full p-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    value={selectedDriver.phone || ''}
                    readOnly
                    className="w-full p-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle Model
                  </label>
                  <input
                    type="text"
                    value={selectedDriver.vehicleModel || ''}
                    readOnly
                    className="w-full p-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vehicle Number
                  </label>
                  <input
                    type="text"
                    value={selectedDriver.vehicleNumber || ''}
                    readOnly
                    className="w-full p-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Total Rides
                  </label>
                  <input
                    type="text"
                    value={selectedDriver.totalRides || '0'}
                    readOnly
                    className="w-full p-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rating
                  </label>
                  <input
                    type="text"
                    value={selectedDriver.ratings ? selectedDriver.ratings.toFixed(1) : '0.0'}
                    readOnly
                    className="w-full p-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Approval Status
                  </label>
                  <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      selectedDriver.isApproved
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {selectedDriver.isApproved ? 'Approved' : 'Pending Approval'}
                    </span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Documents Status
                  </label>
                  <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      selectedDriver.documentsVerified
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {selectedDriver.documentsVerified ? 'Verified' : 'Pending Verification'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Documents Section */}
              <div className="mt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Documents</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'aadhaarCard', label: 'Aadhaar Card' },
                    { key: 'driversLicense', label: 'Driver\'s License' },
                    { key: 'driverPhoto', label: 'Driver Photo' },
                    { key: 'vehicleRC', label: 'Vehicle RC' },
                    { key: 'insuranceCertificate', label: 'Insurance Certificate' },
                    { key: 'pucCertificate', label: 'PUC Certificate' },
                    { key: 'vehiclePhotoFront', label: 'Vehicle Photo (Front)' },
                    { key: 'vehiclePhotoBack', label: 'Vehicle Photo (Back)' }
                  ].map(({ key, label }) => (
                    <div key={key} className="border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">{label}</span>
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                          selectedDriver.documents?.[key]
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {selectedDriver.documents?.[key] ? 'Uploaded' : 'Not Uploaded'}
                        </span>
                      </div>
                      {selectedDriver.documents?.[key] && (
                        <button
                          onClick={() => window.open(selectedDriver.documents[key], '_blank')}
                          className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          View Document
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Document Modal */}
      {showDocumentModal && selectedDriver && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4 overflow-y-auto max-h-[90vh]">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">
                Documents for {selectedDriver.name || 'Unknown'}
              </h2>
              <button
                onClick={() => setShowDocumentModal(false)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <HiXCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900">Document Verification Status</h3>
                    <p className="text-sm text-gray-600">
                      {selectedDriver.documentsVerified ? 'Documents are verified' : 'Documents pending verification'}
                    </p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    selectedDriver.documentsVerified
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {selectedDriver.documentsVerified ? 'Verified' : 'Pending'}
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-medium text-gray-900">Driver Status</h3>
                    <p className="text-sm text-gray-600">
                      Vendor drivers are automatically approved and ready to take rides once documents are verified.
                    </p>
                  </div>
                  <div className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                    Ready to Drive
                  </div>
                </div>

                <p className="text-sm text-gray-600 mt-4">
                  Vendor drivers are automatically approved. Document verification is handled by admin for compliance purposes only.
                </p>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowDocumentModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Driver Assignment Modal */}
      {showAssignModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full m-4 overflow-y-auto max-h-[90vh] border border-gray-200">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  Assign Driver to Booking
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Booking ID: #{selectedBooking.bookingId}
                </p>
              </div>
              <button
                onClick={() => setShowAssignModal(false)}
                className="text-gray-400 hover:text-gray-600 focus:outline-none transition-colors duration-200 p-2 hover:bg-gray-100 rounded-full"
              >
                <HiXCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              {/* Enhanced Booking Details */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 mb-6 border border-blue-200">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-blue-500 rounded-lg mr-3">
                    <HiDocument className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Booking Details</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Passenger</p>
                    <p className="font-semibold text-gray-900">{selectedBooking.user?.name || 'N/A'}</p>
                    <p className="text-sm text-gray-600">{selectedBooking.user?.phone || 'N/A'}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Pickup Location</p>
                    <p className="font-semibold text-gray-900 text-sm">
                      📍 {selectedBooking.pickupLocation?.name || selectedBooking.from}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Drop Location</p>
                    <p className="font-semibold text-gray-900 text-sm">
                      🏁 {selectedBooking.dropLocation?.name || selectedBooking.to}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date & Time</p>
                    <p className="font-semibold text-gray-900">
                      {new Date(selectedBooking.pickupDate).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-600">{selectedBooking.pickupTime}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Trip Amount</p>
                    <p className="font-bold text-green-600 text-lg">₹{selectedBooking.totalAmount}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Status</p>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                      Pending Assignment
                    </span>
                  </div>
                </div>
              </div>

              {/* Enhanced Available Drivers */}
              <div>
                <div className="flex items-center mb-6">
                  <div className="p-2 bg-green-500 rounded-lg mr-3">
                    <HiUsers className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Select Available Driver</h3>
                    <p className="text-sm text-gray-600">Click on a driver to assign them to this booking</p>
                  </div>
                </div>
                {drivers.filter(driver => driver.isAvailable && !driver.currentBooking).length > 0 ? (
                  <div className="grid gap-4">
                    {drivers
                      .filter(driver => driver.isAvailable && !driver.currentBooking)
                      .map((driver) => (
                        <div
                          key={driver._id}
                          className={`border-2 border-gray-200 rounded-xl p-5 hover:border-indigo-300 hover:bg-indigo-50 cursor-pointer transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg ${
                            assigningDriver ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          onClick={() => !assigningDriver && assignDriverToBooking(driver._id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg shadow-lg">
                                {driver.name?.charAt(0) || 'D'}
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900 text-lg">{driver.name || 'Unknown'}</div>
                                <div className="text-sm text-gray-600 flex items-center">
                                  <HiPhone className="h-4 w-4 mr-1" />
                                  {driver.phone || 'No phone'}
                                </div>
                              </div>
                            </div>
                            <div className="text-center">
                              <div className="font-semibold text-gray-900 flex items-center">
                                <HiTruck className="h-4 w-4 mr-1" />
                                {driver.vehicleNumber || 'N/A'}
                              </div>
                              <div className="text-sm text-gray-600">{driver.vehicleModel || 'N/A'}</div>
                            </div>
                            <div className="flex flex-col items-end space-y-2">
                              {assigningDriver ? (
                                <div className="flex items-center px-3 py-1">
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-200 border-t-indigo-600 mr-2"></div>
                                  <span className="text-xs font-medium text-indigo-600">Assigning...</span>
                                </div>
                              ) : (
                                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 shadow-sm">
                                  ✅ Available
                                </span>
                              )}
                              <div className="flex items-center bg-yellow-50 px-2 py-1 rounded-lg">
                                <HiStar className="h-4 w-4 text-yellow-500 mr-1" />
                                <span className="text-sm font-semibold text-yellow-700">
                                  {driver.ratings ? driver.ratings.toFixed(1) : '0.0'}
                                </span>
                              </div>
                              <div className="text-xs text-gray-500">
                                {driver.totalRides || 0} rides
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                    <div className="mx-auto h-20 w-20 rounded-full bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center mb-6 shadow-lg">
                      <HiExclamation className="h-10 w-10 text-orange-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">No Available Drivers</h3>
                    <p className="text-gray-600 max-w-md mx-auto leading-relaxed">
                      All your drivers are currently unavailable or on active trips. Please check back in a few minutes or contact your drivers directly.
                    </p>
                    <div className="mt-6 flex justify-center space-x-3">
                      <button
                        onClick={() => {
                          setShowAssignModal(false);
                          setActiveTab('drivers');
                        }}
                        className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors font-medium text-sm"
                      >
                        Manage Drivers
                      </button>
                      <button
                        onClick={() => fetchDashboardData()}
                        className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-medium text-sm"
                      >
                        Refresh Status
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-8 flex justify-between items-center pt-6 border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  💡 Tip: Select the best available driver based on their rating and proximity
                </div>
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="px-6 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorDashboard;
